// src/pages/HeadOfSupplyChain.js
import React from 'react';
import '../HumanResources.css';

function HeadOfSupplyChain() {
  return (
    <div className="hr-page">
      <h1>HEAD OF SUPPLY CHAIN</h1>

      {/* Overview Section */}
      <h2>Overview</h2>
      <p>
        The Head of Supply Chain is responsible for the entire process of getting products from suppliers to customers efficiently. Their key responsibilities include:
      </p>
      <ul>
        <li>
          <strong>Procurement and Supplier Management:</strong> Ensuring a reliable source of materials.
        </li>
        <li>
          <strong>Managing Imports, Storage, Exports, and Logistics:</strong> Ensuring the smooth flow of goods.
        </li>
        <li>
          <strong>Overseeing Loading and Unloading of Stock:</strong> Managing the physical handling of stock—a key operational task.
        </li>
        <li>
          <strong>Inventory Control:</strong> Ensuring the right amount of stock is available to meet demand without overstocking.
        </li>
        <li>
          <strong>Coordinating Customs Clearing:</strong> Facilitating international trade by coordinating clearance of imports and exports.
        </li>
        <li>
          <strong>Customer Communication:</strong> Keeping customers informed regarding deliveries.
        </li>
        <li>
          <strong>Sales Support:</strong> Initiating sales by issuing proforma invoices and placing orders for materials.
        </li>
        <li>
          <strong>Sales Documentation:</strong> Preparing documents such as delivery notes, packing lists, and Certificates of Analysis (COAs) to ensure accurate order fulfillment.
        </li>
      </ul>

      {/* Qualifications Section */}
      <h2>Qualifications</h2>
      <ul>
        <li>Bachelor&apos;s degree in Supply Chain Management, Logistics, Business Administration, Engineering, or a related field. A Master&apos;s degree is highly preferred.</li>
        <li>[Number] + years of progressive experience in supply chain management, with at least [Number] years in a senior leadership role.</li>
        <li>Proven track record of developing and implementing successful supply chain strategies that have delivered measurable results.</li>
        <li>Deep understanding of end-to-end supply chain processes, including sourcing, procurement, logistics, warehousing, and distribution.</li>
        <li>Strong negotiation, contract management, and supplier relationship management skills.</li>
        <li>Excellent analytical, problem-solving, and decision-making skills, with the ability to interpret complex data and develop actionable insights.</li>
        <li>Proficiency in supply chain management software and technology (e.g., ERP, TMS, WMS).</li>
        <li>Strong leadership, communication (both written and verbal), and interpersonal skills, with the ability to influence and build relationships at all levels.</li>
        <li>Ability to thrive in a fast-paced and dynamic environment.</li>
        <li>[Mention any specific industry experience or certifications that are preferred, e.g., APICS certifications (CPIM, CSCP)].</li>
      </ul>

      {/* Proposed Job Levels Section */}
      <h2>Proposed Job Levels for the Head of Supply Chain Role</h2>
      <p>
        This structure provides a potential career path within the supply chain function at Nesim, starting from operational tasks to strategic leadership. It is primarily based on the description provided by the current Head of Supply Chain, with logical inferences for more junior and senior roles.
      </p>

      {/* Level 1 */}
      <h3>Level 1: Supply Chain Coordinator/Specialist</h3>
      <ul>
        <li><strong>Focus:</strong> Primarily on the operational and administrative tasks within the supply chain.</li>
        <li>
          <strong>Key Responsibilities:</strong>
          <ul>
            <li>Communicating with suppliers on routine matters.</li>
            <li>Assisting with unloading and loading stock.</li>
            <li>Monitoring imports, storage, shipment, and exports by tracking their status.</li>
            <li>Counting stocks and ensuring that warehouse records are accurate.</li>
            <li>Communicating with customers regarding order status and deliveries.</li>
            <li>Preparing basic documentation required for a sale, such as Delivery Notes and Packing Lists.</li>
            <li>Assisting with inventory control by providing data on stock levels.</li>
            <li>Following up with forwarders to ensure timely arrival of stock.</li>
          </ul>
        </li>
      </ul>

      {/* Level 2 */}
      <h3>Level 2: Supply Chain Manager</h3>
      <ul>
        <li><strong>Focus:</strong> Taking ownership of key supply chain functions and coordinating activities across different stages.</li>
        <li>
          <strong>Key Responsibilities:</strong>
          <ul>
            <li>
              <strong>Procurement:</strong> Placing orders for materials and keeping track of products to ensure timely delivery.
            </li>
            <li>
              <strong>Procurement Strategy:</strong> Defining and implementing procurement strategies.
            </li>
            <li>
              <strong>Inventory Control:</strong> Actively managing inventory levels to prevent shortages and overstocking.
            </li>
            <li>
              <strong>Storage and Warehouse Management:</strong> Ensuring efficient and secure storage of goods.
            </li>
            <li>
              <strong>Logistics Coordination:</strong> Coordinating the movement of goods from suppliers to the warehouse and from the warehouse to customers, optimizing transportation and delivery processes.
            </li>
            <li>
              <strong>Imports and Exports Management:</strong> Overseeing all import and export activities.
            </li>
            <li>
              <strong>Customs Clearing:</strong> Managing the customs clearing process for all imports and exports.
            </li>
            <li>
              <strong>Supplier Relationship Management:</strong> Building and maintaining relationships with key suppliers.
            </li>
            <li>
              <strong>Customer Communication:</strong> Ensuring timely and accurate communication with customers regarding their orders.
            </li>
            <li>
              Preparing comprehensive sales documentation, including Delivery Notes, Packing Lists, and Certificates of Analysis (COA).
            </li>
            <li>
              Collaborating with various departments (finance, sales, etc.) to ensure alignment and smooth operations.
            </li>
            <li>
              Monitoring imports, storage, shipment, and exports and proactively addressing any potential issues.
            </li>
          </ul>
        </li>
      </ul>

      {/* Level 3 */}
      <h3>Level 3: Director of Supply Chain (Potential Future Level)</h3>
      <ul>
        <li><strong>Focus:</strong> Setting the overall strategic direction for the supply chain at a higher level, aligning it with the company's long-term objectives and exploring opportunities for optimization and expansion.</li>
        <li>
          <strong>Potential Responsibilities:</strong>
          <ul>
            <li>Developing and implementing long-term supply chain strategies.</li>
            <li>Identifying and mitigating potential risks in the supply chain.</li>
            <li>Evaluating and implementing new technologies to improve supply chain efficiency.</li>
            <li>Expanding the supplier network and exploring new sourcing opportunities.</li>
            <li>Overseeing the performance of the entire supply chain team and driving continuous improvement.</li>
            <li>Contributing to the overall business strategy as part of the senior management team.</li>
          </ul>
        </li>
      </ul>
    </div>
  );
}

export default HeadOfSupplyChain;
